import { Popover as BaseUiPopover } from "@base-ui/react/popover"
import React from "react"

import classNames from "classnames"

import { PopoverArrow } from "shared/ui/popover/ui/PopoverArrow"

import "./styles/_popover.scss"

export interface PopoverProps extends BaseUiPopover.Root.Props {}

const PopoverRoot = React.memo((props: PopoverProps) => {
  return <BaseUiPopover.Root {...props} />
})

PopoverRoot.displayName = "Popover.Root"

interface PopoverTriggerProps extends BaseUiPopover.Trigger.Props {
  className?: string
}

const PopoverTrigger = React.memo(
  React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
    ({ className, ...props }, ref) => {
      return (
        <BaseUiPopover.Trigger
          ref={ref}
          className={classNames("popover__trigger", className)}
          {...props}
        />
      )
    }
  )
)

PopoverTrigger.displayName = "Popover.Trigger"

interface PopoverPortalProps extends BaseUiPopover.Portal.Props {}

const PopoverPortal = React.memo((props: PopoverPortalProps) => {
  return <BaseUiPopover.Portal {...props} />
})

PopoverPortal.displayName = "Popover.Portal"

interface PopoverPositionerProps extends BaseUiPopover.Positioner.Props {
  className?: string
}

const PopoverPositioner = React.memo(
  ({
    className,
    side = "bottom",
    align = "center",
    sideOffset = 8,
    ...props
  }: PopoverPositionerProps) => {
    return (
      <BaseUiPopover.Positioner
        className={classNames("popover__positioner", className)}
        side={side}
        align={align}
        sideOffset={sideOffset}
        {...props}
      />
    )
  }
)

PopoverPositioner.displayName = "Popover.Positioner"

interface PopoverPopupProps extends BaseUiPopover.Popup.Props {
  className?: string
}

const PopoverPopup = React.memo(
  ({ className, ...props }: PopoverPopupProps) => {
    return (
      <BaseUiPopover.Popup
        className={classNames("popover__popup", className)}
        {...props}
      />
    )
  }
)

PopoverPopup.displayName = "Popover.Popup"

interface PopoverArrowProps extends BaseUiPopover.Arrow.Props {
  className?: string
  fillClassName?: string
  outerStrokeClassName?: string
  innerStrokeClassName?: string
}

const PopoverArrowComponent = React.memo(
  ({
    className,
    fillClassName = "popover__arrow-fill",
    outerStrokeClassName = "popover__arrow-outer-stroke",
    innerStrokeClassName = "popover__arrow-inner-stroke",
    ...props
  }: PopoverArrowProps) => {
    return (
      <BaseUiPopover.Arrow
        className={classNames("popover__arrow", className)}
        {...props}
      >
        <PopoverArrow
          fillClassName={fillClassName}
          outerStrokeClassName={outerStrokeClassName}
          innerStrokeClassName={innerStrokeClassName}
        />
      </BaseUiPopover.Arrow>
    )
  }
)

PopoverArrowComponent.displayName = "Popover.Arrow"

interface PopoverCloseProps extends BaseUiPopover.Close.Props {
  className?: string
}

const PopoverClose = React.memo(
  ({ className, ...props }: PopoverCloseProps) => {
    return (
      <BaseUiPopover.Close
        className={classNames("popover__close", className)}
        {...props}
      />
    )
  }
)

PopoverClose.displayName = "Popover.Close"

interface PopoverTitleProps extends BaseUiPopover.Title.Props {
  className?: string
}

const PopoverTitle = React.memo(
  ({ className, ...props }: PopoverTitleProps) => {
    return (
      <BaseUiPopover.Title
        className={classNames("popover__title", className)}
        {...props}
      />
    )
  }
)

PopoverTitle.displayName = "Popover.Title"

interface PopoverDescriptionProps extends BaseUiPopover.Description.Props {
  className?: string
}

const PopoverDescription = React.memo(
  ({ className, ...props }: PopoverDescriptionProps) => {
    return (
      <BaseUiPopover.Description
        className={classNames("popover__description", className)}
        {...props}
      />
    )
  }
)

PopoverDescription.displayName = "Popover.Description"

export const Popover = Object.assign(PopoverRoot, {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Portal: PopoverPortal,
  Positioner: PopoverPositioner,
  Popup: PopoverPopup,
  Arrow: PopoverArrowComponent,
  Close: PopoverClose,
  Title: PopoverTitle,
  Description: PopoverDescription
})
